import { useRef, useState } from "react";
import { api, fmtErr } from "../lib/api";
import { toast } from "sonner";
import { EnvelopeSimpleOpen, Spinner, UploadSimple, UserMinus, UserPlus, X } from "@phosphor-icons/react";
import { StatusBadge } from "./StatusBadge";

const REASONS = {
  left_company: { label: "Left the company", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  on_leave: { label: "On leave", cls: "bg-sky-50 text-sky-700 border-sky-200" },
  mailbox_unmonitored: { label: "Mailbox unmonitored", cls: "bg-amber-50 text-amber-700 border-amber-200" },
  other: { label: "Other", cls: "bg-zinc-50 text-zinc-600 border-zinc-200" },
};

export function ReplyLoggerCard() {
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [applying, setApplying] = useState(false);
  const [result, setResult] = useState(null);
  const [selMatch, setSelMatch] = useState(null);
  const [markLeft, setMarkLeft] = useState(true);
  const [reps, setReps] = useState([]);
  const [dragging, setDragging] = useState(false);
  const fileRef = useRef(null);

  const reset = () => { setResult(null); setText(""); setSelMatch(null); setReps([]); };

  const analyse = async (fn) => {
    setBusy(true);
    try {
      const r = await fn();
      setResult(r);
      const first = r.matches[0];
      setSelMatch(first?.id || null);
      setMarkLeft(!!first && first.status !== "left_company" &&
        (r.parsed.reason === "left_company" || r.parsed.reason === "mailbox_unmonitored"));
      setReps((r.parsed.replacements || []).map(x => ({ ...x, include: !x.exists })));
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setBusy(false);
    }
  };

  const onFile = (f) => {
    if (!f) {
      toast.error("That drop didn't include a real file — Outlook drags need saving first. Drag the email to your desktop (creating a .msg file), then drop that here — or paste the reply text.");
      return;
    }
    if (f.size === 0) {
      toast.error("The file arrived empty — this happens with emails dragged straight from Outlook or OneDrive online-only files. Save it to a local folder first, then upload it.");
      return;
    }
    analyse(() => api.repliesParseFile(f));
    if (fileRef.current) fileRef.current.value = "";
  };
  const setRep = (i, k, v) => setReps(prev => prev.map((r, j) => j === i ? { ...r, [k]: v } : r));

  const apply = async () => {
    const chosen = result.matches.find(m => m.id === selMatch);
    const doLeft = markLeft && !!chosen && chosen.status !== "left_company";
    const toCreate = reps.filter(r => r.include && !r.exists);
    if (!doLeft && !toCreate.length) { toast.info("Nothing selected to apply"); return; }
    setApplying(true);
    try {
      const r = await api.repliesApply({
        contact_id: doLeft ? selMatch : null,
        mark_left: doLeft,
        left_note: result.parsed.summary || "",
        replacements: toCreate,
      });
      const bits = [];
      if (r.marked_left) bits.push(`${chosen.first_name} ${chosen.last_name} marked as left`);
      if (r.created.length) bits.push(`${r.created.length} replacement contact${r.created.length === 1 ? "" : "s"} added`);
      (r.skipped || []).forEach(s => toast.warning(`${s.email}: ${s.reason}`));
      if (bits.length) toast.success(bits.join(" · "));
      reset();
    } catch (e) {
      toast.error(fmtErr(e));
    } finally {
      setApplying(false);
    }
  };

  const parsed = result?.parsed;
  const reason = parsed ? (REASONS[parsed.reason] || REASONS.other) : null;

  return (
    <div
      className={`rounded-lg border p-4 space-y-3 transition-colors ${dragging ? "border-orange-400 border-dashed bg-orange-50/40" : "border-zinc-200 bg-white"}`}
      data-testid="reply-logger-card"
      onDragOver={(e) => { if (!result && !busy) { e.preventDefault(); setDragging(true); } }}
      onDragLeave={(e) => { if (!e.currentTarget.contains(e.relatedTarget)) setDragging(false); }}
      onDrop={(e) => { if (!result && !busy) { e.preventDefault(); setDragging(false); onFile(e.dataTransfer.files?.[0]); } }}
    >
      <div className="flex items-center gap-1.5 text-sm font-medium">
        <EnvelopeSimpleOpen size={15} className="text-orange-500" /> Turn an auto-reply into contact updates
      </div>
      <p className="text-xs text-zinc-500">
        Paste any auto-reply you received — "no longer with the company", out-of-office, unmonitored mailbox — or <span className="font-medium text-zinc-700">drag &amp; drop the Outlook .msg / .eml file anywhere on this card</span>. The AI reads it, finds the matching contact in MediHub, and prepares the updates for you to approve: mark the leaver so they're excluded from future sends, and add any named replacement as a new contact.
      </p>
      {dragging && (
        <div className="rounded-md border border-dashed border-orange-300 bg-orange-50 px-3 py-2 text-xs text-orange-700 font-medium" data-testid="reply-drop-hint">
          Drop the .msg / .eml file to analyse it
        </div>
      )}

      {!result && (
        <>
          <textarea value={text} onChange={e => setText(e.target.value)} rows={4} disabled={busy}
            placeholder={'e.g. "Thank you for your email. I am no longer working at Prime — please contact my line manager, Jane Doe at jane.doe@…"'}
            className="w-full rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-zinc-300 resize-y"
            data-testid="reply-text-input" />
          <div className="flex items-center gap-2">
            <button onClick={() => analyse(() => api.repliesParse(text))} disabled={busy || text.trim().length < 20}
              data-testid="reply-analyse-btn"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-40">
              {busy ? <Spinner size={13} className="animate-spin" /> : <EnvelopeSimpleOpen size={13} />}
              {busy ? "Reading the reply…" : "Analyse reply"}
            </button>
            <button onClick={() => fileRef.current?.click()} disabled={busy} data-testid="reply-file-btn"
              className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-zinc-200 text-zinc-700 hover:border-zinc-400 disabled:opacity-40">
              <UploadSimple size={13} /> Upload .msg / .eml
            </button>
            <input ref={fileRef} type="file" accept=".msg,.eml,.txt" className="hidden" data-testid="reply-file-input"
              onChange={e => onFile(e.target.files?.[0])} />
          </div>
        </>
      )}

      {result && (
        <div className="space-y-3" data-testid="reply-result">
          <div className="flex items-start gap-2">
            <span className={`shrink-0 text-[11px] px-2 py-0.5 rounded-full border font-medium ${reason.cls}`} data-testid="reply-reason-badge">
              {reason.label}{parsed.left_date ? ` · ${parsed.left_date}` : ""}
            </span>
            <p className="text-xs text-zinc-600 flex-1" data-testid="reply-summary">{parsed.summary}</p>
            <button onClick={reset} className="text-zinc-400 hover:text-zinc-600" title="Start again" data-testid="reply-reset-btn"><X size={15} /></button>
          </div>

          {!parsed.is_leaver_reply && (
            <p className="text-xs text-amber-600 border border-amber-200 bg-amber-50 rounded-md px-2.5 py-1.5">
              This doesn't look like a leaver notice — double-check before applying anything.
            </p>
          )}

          <div className="rounded-md border border-zinc-100 bg-zinc-50/60 p-3 space-y-2">
            <div className="flex items-center gap-1.5 text-xs font-medium text-zinc-700"><UserMinus size={13} className="text-orange-500" /> Who left</div>
            {result.matches.length === 0 && (
              <p className="text-xs text-zinc-500" data-testid="reply-no-match">
                No matching contact found for {parsed.person?.email || `${parsed.person?.first_name} ${parsed.person?.last_name}`} — nothing to mark.
              </p>
            )}
            {result.matches.map(m => (
              <label key={m.id} className="flex items-center gap-2 text-xs cursor-pointer" data-testid={`reply-match-${m.id}`}>
                <input type="radio" name="reply-match" checked={selMatch === m.id} onChange={() => setSelMatch(m.id)} />
                <span className="font-medium text-zinc-900">{m.first_name} {m.last_name}</span>
                <span className="font-mono text-[11px] text-zinc-500">{m.email}</span>
                {m.company && <span className="text-zinc-500">· {m.company}</span>}
                <span className="text-[10px] px-1.5 py-px rounded-full border border-zinc-200 bg-white text-zinc-500">{m.match}</span>
                {m.status !== "active" && <StatusBadge value={m.status} />}
              </label>
            ))}
            {result.matches.length > 0 && (
              <label className="flex items-center gap-2 text-xs font-medium text-zinc-800 pt-1 cursor-pointer">
                <input type="checkbox" checked={markLeft} onChange={e => setMarkLeft(e.target.checked)}
                  disabled={result.matches.find(m => m.id === selMatch)?.status === "left_company"}
                  data-testid="reply-mark-left-checkbox" />
                Mark as "no longer at company" — excluded from all future sends
                {result.matches.find(m => m.id === selMatch)?.status === "left_company" && (
                  <span className="text-[10px] text-orange-600 font-normal">already marked as left</span>
                )}
              </label>
            )}
          </div>

          {reps.length > 0 && (
            <div className="rounded-md border border-zinc-100 bg-zinc-50/60 p-3 space-y-2">
              <div className="flex items-center gap-1.5 text-xs font-medium text-zinc-700"><UserPlus size={13} className="text-emerald-600" /> Suggested replacement{reps.length === 1 ? "" : "s"}</div>
              {reps.map((r, i) => (
                <div key={i} className="flex flex-wrap items-center gap-1.5" data-testid={`reply-replacement-${i}`}>
                  <input type="checkbox" checked={r.include} disabled={r.exists} onChange={e => setRep(i, "include", e.target.checked)}
                    data-testid={`reply-replacement-${i}-include`} />
                  {["first_name", "last_name", "email", "job_title", "company"].map(k => (
                    <input key={k} value={r[k]} onChange={e => setRep(i, k, e.target.value)} disabled={r.exists}
                      placeholder={k.replace("_", " ")}
                      data-testid={`reply-replacement-${i}-${k}`}
                      className={`text-xs rounded border border-zinc-200 px-2 py-1 bg-white disabled:bg-zinc-100 disabled:text-zinc-400 ${k === "email" ? "font-mono w-56" : "w-32"}`} />
                  ))}
                  {r.exists && <span className="text-[10px] px-1.5 py-px rounded-full border border-emerald-200 bg-emerald-50 text-emerald-700">already in contacts</span>}
                  {!r.exists && r.name_guessed && <span className="text-[10px] px-1.5 py-px rounded-full border border-amber-200 bg-amber-50 text-amber-700" data-testid={`reply-replacement-${i}-guessed`}>name guessed from address — check</span>}
                </div>
              ))}
              <p className="text-[11px] text-zinc-400">First name, last name and company are required to add a contact — edit the boxes if the reply didn't include them.</p>
            </div>
          )}

          <button onClick={apply} disabled={applying} data-testid="reply-apply-btn"
            className="inline-flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-zinc-950 text-white hover:bg-zinc-800 disabled:opacity-40">
            {applying ? <Spinner size={13} className="animate-spin" /> : null}
            {applying ? "Applying…" : "Apply updates"}
          </button>
        </div>
      )}
    </div>
  );
}
